import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * FE-FIX04. После открытия события и возврата назад список возвращался наверх.
 *
 * Позиция хранится в history.state, а не в sessionStorage: она относится к
 * конкретной записи истории. Прямой вход по ссылке в карточку истории не
 * создаёт, поэтому возврат оттуда ничего не восстанавливает — так и нужно.
 *
 * Восстановление однократное и только при совпадении listUrl: тот же список
 * с другими фильтрами — это другой список, чужую позицию туда переносить
 * нельзя. Приоритет — строка самого события (после неё виден контекст),
 * scrollY — фоллбэк, когда строки в текущей выборке уже нет.
 */
const readRestoreState = () => {
  const state = typeof window === 'undefined' ? null : window.history.state;
  const restore = state?.listRestore;
  return restore && typeof restore === 'object' ? restore : null;
};

export const useListScrollRestore = ({ listReady, rowRefs }) => {
  const location = useLocation();
  const listUrl = `${location.pathname}${location.search}`;
  const restoredRef = useRef(false);

  const rememberPosition = useCallback((eventId) => {
    if (typeof window === 'undefined') return;
    const state = window.history.state || {};
    window.history.replaceState(
      { ...state, listRestore: { listUrl, scrollY: window.scrollY, eventId } },
      ''
    );
  }, [listUrl]);

  useEffect(() => {
    if (!listReady || restoredRef.current) return;

    const restore = readRestoreState();
    if (!restore || restore.listUrl !== listUrl) return;

    restoredRef.current = true;

    const row = rowRefs?.current?.get(restore.eventId);
    if (row?.scrollIntoView) {
      row.scrollIntoView({ block: 'center' });
      return;
    }
    if (Number.isFinite(restore.scrollY)) window.scrollTo(0, restore.scrollY);
  }, [listReady, listUrl, rowRefs]);

  return { rememberPosition };
};

export default useListScrollRestore;
